import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  Brain, 
  MessageSquare, 
  Target, 
  Heart, 
  FileText, 
  Mic, 
  TrendingUp, 
  Eye, 
  PenTool, 
  MessageCircle, 
  Users, 
  Shield, 
  GraduationCap, 
  Globe, 
  Scale, 
  BarChart3,
  ArrowLeft,
  CheckCircle,
  Lightbulb,
  Star,
  Zap
} from 'lucide-react';

export default function AISpecificServices() {
  const { serviceType } = useParams();

  const serviceData = {
    'predictive-analytics': {
      title: 'Predictive Analytics',
      icon: TrendingUp,
      description: 'Leverages historical data to forecast outcomes and trends, empowering proactive business decisions and risk reduction.',
      keyBenefits: [
        'Proactive decision making based on data trends',
        'Risk reduction through early warning systems',
        'Improved resource allocation and planning',
        'Enhanced competitive advantage through foresight'
      ],
      useCases: [
        'Predicting hospital admission surges',
        'Forecasting loan default risks',
        'Spotting customers likely to cancel a service',
        'Anticipating regional housing demand'
      ]
    },
    'chatbots-virtual-assistants': {
      title: 'Chatbots and Virtual Assistants',
      icon: MessageSquare,
      description: 'Automates real-time customer interactions to provide instant support, reduce costs, and improve user satisfaction 24/7.',
      keyBenefits: [
        '24/7 customer support availability',
        'Reduced operational costs',
        'Instant response to customer queries',
        'Scalable customer service solution'
      ],
      useCases: [
        'Booking medical appointments online',
        'Handling donation and volunteer inquiries',
        'Answering FAQs for events',
        'Guiding users through property searches'
      ]
    },
    'personalized-recommendations': {
      title: 'Personalized Recommendations',
      icon: Target,
      description: 'Suggests content, products, or services based on user behavior. Improves customer satisfaction and boosts sales.',
      keyBenefits: [
        'Increased customer satisfaction through relevance',
        'Higher conversion rates and sales',
        'Enhanced user engagement',
        'Improved customer retention'
      ],
      useCases: [
        'Recommending treatments based on patient history',
        'Suggesting credit cards tailored to spending patterns',
        'Showing donation campaigns aligned with donor interests',
        'Streaming platforms suggesting shows users will enjoy'
      ]
    },
    'sentiment-analysis': {
      title: 'Sentiment Analysis',
      icon: Heart,
      description: 'Captures emotions in text to monitor feedback, detect brand sentiment, and gauge public opinion effectively.',
      keyBenefits: [
        'Real-time feedback monitoring',
        'Brand reputation management',
        'Improved customer understanding',
        'Data-driven decision making'
      ],
      useCases: [
        'Analyzing patient survey responses',
        'Monitoring online reviews of financial products',
        'Gauging social media sentiment during campaigns',
        'Identifying emotional tone in customer support chats'
      ]
    },
    'document-summarization': {
      title: 'Document Summarization',
      icon: FileText,
      description: 'Condenses lengthy text into digestible summaries, enabling faster understanding, improved productivity, and decision-making.',
      keyBenefits: [
        'Faster information processing',
        'Improved productivity and efficiency',
        'Better decision-making with key insights',
        'Time savings on document review'
      ],
      useCases: [
        'Summarizing patient medical history',
        'Extracting key points from financial reports',
        'Condensing long research papers',
        'Creating bullet-point property summaries'
      ]
    },
    'speech-text-conversion': {
      title: 'Speech-to-Text and Text-to-Speech',
      icon: Mic,
      description: 'Bridges the gap between spoken and written language to improve accessibility, automate communication, and streamline workflows.',
      keyBenefits: [
        'Enhanced accessibility for all users',
        'Streamlined documentation processes',
        'Automated transcription services',
        'Improved workflow efficiency'
      ],
      useCases: [
        'Doctors dictating medical notes',
        'Reading out bank balances through voice assistants',
        'Transcribing interviews or podcasts',
        'Auto-generating property listings from voice notes'
      ]
    },
    'demand-forecasting': {
      title: 'Demand Forecasting',
      icon: BarChart3,
      description: 'Anticipates future product or service demand, helping businesses optimize inventory, staffing, and strategic planning.',
      keyBenefits: [
        'Optimized inventory management',
        'Better resource allocation',
        'Reduced waste and costs',
        'Improved strategic planning'
      ],
      useCases: [
        'Estimating flu medication needs',
        'Forecasting ticket sales for events',
        'Planning food supplies during emergencies',
        'Predicting property interest by area'
      ]
    },
    'vision-ai': {
      title: 'Vision AI for Image and Video Processing',
      icon: Eye,
      description: 'Empowers machines to interpret images and videos, enhancing quality control, diagnostics, and media organization.',
      keyBenefits: [
        'Automated visual analysis',
        'Enhanced quality control',
        'Improved diagnostic accuracy',
        'Efficient media organization'
      ],
      useCases: [
        'Detecting tumors in medical scans',
        'Verifying user ID documents',
        'Reviewing drone footage of disaster areas',
        'Auto-tagging and enhancing property photos'
      ]
    },
    'content-creation': {
      title: 'Intelligent Content Creation',
      icon: PenTool,
      description: 'Generates written content on demand — from blogs to ads — accelerating content pipelines and maintaining brand voice.',
      keyBenefits: [
        'Accelerated content production',
        'Consistent brand voice maintenance',
        'Cost-effective content creation',
        'Scalable marketing materials'
      ],
      useCases: [
        'Writing weekly newsletters',
        'Creating product descriptions for e-commerce',
        'Generating ad copy variations',
        'Producing SEO blog articles'
      ]
    },
    'natural-language-generation': {
      title: 'Natural Language Generation (NLG)',
      icon: MessageCircle,
      description: 'Transforms raw data into human-like language, turning complex information into clear, usable narratives and summaries.',
      keyBenefits: [
        'Data-to-text automation',
        'Clear communication of complex data',
        'Reduced manual reporting time',
        'Consistent narrative generation'
      ],
      useCases: [
        'Auto-writing monthly performance reports',
        'Summarizing survey results',
        'Drafting investor updates',
        'Generating project status summaries'
      ]
    },
    'customer-behavior-modeling': {
      title: 'Customer Behavior Modeling',
      icon: Users,
      description: 'Analyzes user activity to uncover behavioral patterns, personalize experiences, and predict future customer actions.',
      keyBenefits: [
        'Deep customer insights',
        'Personalized user experiences',
        'Predictive customer analytics',
        'Improved marketing effectiveness'
      ],
      useCases: [
        'Targeting customers likely to make repeat purchases',
        'Identifying users at risk of leaving',
        'Recommending upgrades or cross-sells',
        'Grouping users into interest-based segments'
      ]
    },
    'fraud-detection': {
      title: 'Fraud Detection',
      icon: Shield,
      description: 'Identifies suspicious patterns in transactions or access data to protect businesses from financial and reputational loss.',
      keyBenefits: [
        'Real-time threat detection',
        'Financial loss prevention',
        'Reputation protection',
        'Automated security monitoring'
      ],
      useCases: [
        'Flagging irregular credit card use',
        'Detecting fake IDs or account setups',
        'Alerting on suspicious login activity',
        'Monitoring financial compliance anomalies'
      ]
    },
    'adaptive-learning': {
      title: 'Adaptive Learning Platforms',
      icon: GraduationCap,
      description: 'Delivers personalized learning experiences by adjusting content to individual performance, pace, and preferences.',
      keyBenefits: [
        'Personalized learning paths',
        'Improved learning outcomes',
        'Real-time performance tracking',
        'Adaptive content delivery'
      ],
      useCases: [
        'Customizing corporate training modules',
        'Recommending learning content in real time',
        'Adjusting difficulty based on student performance',
        'Tracking and adapting employee learning progress'
      ]
    },
    'multi-lingual-translation': {
      title: 'Multi-Lingual Translation',
      icon: Globe,
      description: 'Automatically translates content across languages, enabling seamless communication and expanding global reach.',
      keyBenefits: [
        'Global market expansion',
        'Seamless cross-language communication',
        'Reduced translation costs',
        'Real-time language processing'
      ],
      useCases: [
        'Translating customer support chats',
        'Localizing websites and product pages',
        'Converting legal documents for global use',
        'Offering multilingual training materials'
      ]
    },
    'legal-document-analysis': {
      title: 'Legal Document Analysis',
      icon: Scale,
      description: 'Extracts key information from legal texts, accelerating contract review, risk assessment, and compliance tracking.',
      keyBenefits: [
        'Accelerated document review',
        'Enhanced risk assessment',
        'Improved compliance tracking',
        'Reduced legal processing time'
      ],
      useCases: [
        'Highlighting risky clauses in contracts',
        'Summarizing lengthy agreements',
        'Identifying missing legal terms',
        'Comparing similar legal documents quickly'
      ]
    },
    'trend-pattern-recognition': {
      title: 'Trend & Pattern Recognition',
      icon: BarChart3,
      description: 'Uncovers emerging patterns in data to reveal insights, detect anomalies, and guide strategic decision-making.',
      keyBenefits: [
        'Early trend identification',
        'Anomaly detection',
        'Strategic decision support',
        'Competitive advantage through insights'
      ],
      useCases: [
        'Discovering new customer preferences',
        'Detecting shifts in product performance',
        'Spotting recurring issues in feedback',
        'Identifying market trends before competitors'
      ]
    }
  };

  const service = serviceData[serviceType];

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center p-8">
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Service Not Found</h1>
          <Link 
            to="/ai-services" 
            className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium text-sm md:text-base"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to AI Services
          </Link>
        </div>
      </div>
    );
  }

  const Icon = service.icon;

  const styles = {
    container: {
      minHeight: '100vh',
      background: 'linear-gradient(to bottom, #f8fafc, #f1f5f9)',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    },
    heroSection: {
      background: 'linear-gradient(135deg, #3b82f6, #1e40af)',
      color: '#ffffff',
      padding: '0',
      position: 'relative',
      overflow: 'hidden'
    },
    heroPattern: {
      position: 'absolute',
      top: '0',
      left: '0',
      right: '0',
      bottom: '0',
      backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
      backgroundSize: '40px 40px',
      opacity: '0.3'
    },
    heroContent: {
      position: 'relative',
      zIndex: '10',
      padding: '60px 16px 40px',
      textAlign: 'center',
      maxWidth: '1200px',
      margin: '0 auto'
    },
    backButton: {
      position: 'absolute',
      top: '12px',
      left: '12px',
      display: 'inline-flex',
      alignItems: 'center',
      color: '#ffffff',
      textDecoration: 'none',
      fontSize: '12px',
      fontWeight: '600',
      padding: '8px 12px',
      borderRadius: '12px',
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      zIndex: '20',
      transform: 'translateY(0)',
      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)'
    },
    iconContainer: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '60px',
      height: '60px',
      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.1))',
      borderRadius: '16px',
      marginBottom: '20px',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
      transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
      cursor: 'pointer',
      animation: 'float 6s ease-in-out infinite'
    },
    title: {
      fontSize: '1.75rem',
      fontWeight: '900',
      marginBottom: '16px',
      lineHeight: '1.1',
      background: 'linear-gradient(135deg, #ffffff, #e2e8f0)',
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent'
    },
    description: {
      fontSize: '0.95rem',
      maxWidth: '600px',
      margin: '0 auto',
      lineHeight: '1.6',
      opacity: '0.95',
      fontWeight: '400',
      padding: '0 16px'
    },
    contentSection: {
      padding: '40px 16px',
      maxWidth: '1200px',
      margin: '0 auto'
    },
    statsSection: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '12px',
      marginBottom: '40px'
    },
    statCard: {
      background: 'linear-gradient(135deg, #ffffff, #f8fafc)',
      padding: '20px 16px',
      borderRadius: '16px',
      textAlign: 'center',
      border: '1px solid #e2e8f0',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      cursor: 'pointer',
      position: 'relative',
      overflow: 'hidden'
    },
    statIcon: {
      width: '32px',
      height: '32px',
      margin: '0 auto 12px',
      color: '#3b82f6'
    },
    statTitle: {
      fontSize: '0.85rem',
      fontWeight: '600',
      color: '#374151',
      marginBottom: '6px'
    },
    statValue: {
      fontSize: '1.25rem',
      fontWeight: '700',
      background: 'linear-gradient(135deg, #3b82f6, #1e40af)',
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent'
    },
    mainGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr',
      gap: '24px',
      marginBottom: '60px'
    },
    card: {
      background: 'linear-gradient(135deg, #ffffff, #f8fafc)',
      borderRadius: '20px',
      padding: '24px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
      position: 'relative',
      overflow: 'hidden',
      cursor: 'pointer',
      transform: 'translateY(0)'
    },
    cardGradient: {
      position: 'absolute',
      top: '0',
      left: '0',
      right: '0',
      height: '4px',
      background: 'linear-gradient(135deg, #3b82f6, #1e40af)'
    },
    cardTitle: {
      fontSize: '1.25rem',
      fontWeight: '700',
      color: '#1f2937',
      marginBottom: '20px',
      display: 'flex',
      alignItems: 'center'
    },
    cardTitleIcon: {
      width: '20px',
      height: '20px',
      marginRight: '8px',
      padding: '4px',
      borderRadius: '6px',
      background: 'linear-gradient(135deg, #3b82f6, #1e40af)',
      color: '#ffffff'
    },
    listItem: {
      display: 'flex',
      alignItems: 'flex-start',
      marginBottom: '12px',
      fontSize: '0.95rem',
      color: '#4b5563',
      lineHeight: '1.5'
    },
    listIcon: {
      width: '16px',
      height: '16px',
      marginRight: '10px',
      marginTop: '2px',
      flexShrink: '0'
    },
    ctaSection: {
      background: 'linear-gradient(135deg, #f8fafc, #e2e8f0)',
      padding: '50px 16px',
      textAlign: 'center',
      position: 'relative'
    },
    ctaCard: {
      background: 'linear-gradient(135deg, #ffffff, #f8fafc)',
      borderRadius: '24px',
      padding: '32px 24px',
      maxWidth: '600px',
      margin: '0 auto',
      border: '1px solid #e2e8f0',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      position: 'relative',
      overflow: 'hidden'
    },
    ctaGradient: {
      position: 'absolute',
      top: '0',
      left: '0',
      right: '0',
      height: '4px',
      background: 'linear-gradient(135deg, #3b82f6, #1e40af)'
    },
    ctaTitle: {
      fontSize: '1.75rem',
      fontWeight: '800',
      background: 'linear-gradient(135deg, #1f2937, #374151)',
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      marginBottom: '16px'
    },
    ctaText: {
      fontSize: '1rem',
      color: '#6b7280',
      marginBottom: '28px',
      lineHeight: '1.6'
    },
    button: {
      background: 'linear-gradient(135deg, #3b82f6, #1e40af)',
      color: '#ffffff',
      fontWeight: '700',
      padding: '14px 28px',
      borderRadius: '12px',
      border: 'none',
      cursor: 'pointer',
      fontSize: '0.95rem',
      textDecoration: 'none',
      display: 'inline-flex',
      alignItems: 'center',
      boxShadow: '0 10px 15px -3px rgba(59, 130, 246, 0.4), 0 4px 6px -2px rgba(59, 130, 246, 0.2)',
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      position: 'relative',
      overflow: 'hidden',
      transform: 'translateY(0)'
    }
  };

  // Responsive media queries in CSS
  const responsiveCSS = `
    /* Tablet styles */
    @media (min-width: 640px) {
      .hero-content {
        padding: 80px 20px 60px !important;
      }
      
      .icon-container {
        width: 80px !important;
        height: 80px !important;
        border-radius: 20px !important;
        margin-bottom: 24px !important;
      }
      
      .icon-container svg {
        width: 40px !important;
        height: 40px !important;
      }
      
      .hero-title {
        font-size: 2.25rem !important;
        margin-bottom: 20px !important;
      }
      
      .hero-description {
        font-size: 1.1rem !important;
        padding: 0 !important;
      }
      
      .back-button {
        top: 16px !important;
        left: 16px !important;
        font-size: 13px !important;
        padding: 10px 16px !important;
      }
      
      .stats-section {
        grid-template-columns: repeat(4, 1fr) !important;
        gap: 16px !important;
        margin-bottom: 60px !important;
      }
      
      .stat-card {
        padding: 24px 16px !important;
      }
      
      .stat-icon {
        width: 40px !important;
        height: 40px !important;
        margin-bottom: 16px !important;
      }
      
      .stat-title {
        font-size: 0.9rem !important;
        margin-bottom: 8px !important;
      }
      
      .stat-value {
        font-size: 1.4rem !important;
      }
      
      .content-section {
        padding: 60px 20px !important;
      }
      
      .main-grid {
        grid-template-columns: repeat(2, 1fr) !important;
        gap: 32px !important;
        margin-bottom: 80px !important;
      }
      
      .main-card {
        padding: 32px !important;
      }
      
      .card-title {
        font-size: 1.5rem !important;
        margin-bottom: 24px !important;
      }
      
      .card-title-icon {
        width: 24px !important;
        height: 24px !important;
        margin-right: 12px !important;
        padding: 6px !important;
        border-radius: 8px !important;
      }
      
      .list-item {
        font-size: 1rem !important;
        margin-bottom: 14px !important;
      }
      
      .list-icon {
        width: 18px !important;
        height: 18px !important;
        margin-right: 12px !important;
      }
      
      .cta-section {
        padding: 70px 20px !important;
      }
      
      .cta-card {
        padding: 48px 32px !important;
        max-width: 700px !important;
      }
      
      .cta-title {
        font-size: 2.25rem !important;
        margin-bottom: 20px !important;
      }
      
      .cta-text {
        font-size: 1.15rem !important;
        margin-bottom: 36px !important;
      }
      
      .cta-button {
        font-size: 1rem !important;
        padding: 16px 32px !important;
      }
    }
    
    /* Desktop styles */
    @media (min-width: 1024px) {
      .hero-content {
        padding: 120px 20px 80px !important;
      }
      
      .icon-container {
        width: 100px !important;
        height: 100px !important;
        border-radius: 24px !important;
        margin-bottom: 32px !important;
      }
      
      .icon-container svg {
        width: 50px !important;
        height: 50px !important;
      }
      
      .hero-title {
        font-size: clamp(2.5rem, 5vw, 4rem) !important;
        margin-bottom: 24px !important;
      }
      
      .hero-description {
        font-size: 1.3rem !important;
        max-width: 800px !important;
      }
      
      .back-button {
        top: 20px !important;
        left: 20px !important;
        font-size: 14px !important;
        padding: 12px 20px !important;
      }
      
      .stats-section {
        gap: 24px !important;
      }
      
      .stat-card {
        padding: 32px 24px !important;
      }
      
      .stat-icon {
        width: 48px !important;
        height: 48px !important;
      }
      
      .stat-title {
        font-size: 1rem !important;
      }
      
      .stat-value {
        font-size: 1.5rem !important;
      }
      
      .content-section {
        padding: 100px 20px !important;
      }
      
      .main-grid {
        grid-template-columns: repeat(auto-fit, minmax(500px, 1fr)) !important;
        gap: 40px !important;
      }
      
      .main-card {
        padding: 40px !important;
      }
      
      .card-title {
        font-size: 1.75rem !important;
      }
      
      .card-title-icon {
        width: 28px !important;
        height: 28px !important;
      }
      
      .list-item {
        font-size: 1.1rem !important;
        margin-bottom: 16px !important;
      }
      
      .list-icon {
        width: 20px !important;
        height: 20px !important;
      }
      
      .cta-section {
        padding: 80px 20px !important;
      }
      
      .cta-card {
        padding: 60px 40px !important;
      }
      
      .cta-title {
        font-size: 2.5rem !important;
      }
      
      .cta-text {
        font-size: 1.2rem !important;
        margin-bottom: 40px !important;
      }
      
      .cta-button {
        font-size: 1.1rem !important;
        padding: 18px 40px !important;
      }
    }
  `;

  return (
    <div style={styles.container}>
      <style>
        {`
          @keyframes float {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            50% { transform: translateY(-10px) rotate(2deg); }
          }
          
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.8; }
          }
          
          @keyframes shimmer {
            0% { background-position: -200px 0; }
            100% { background-position: calc(200px + 100%) 0; }
          }
          
          .back-button:hover {
            background-color: rgba(255, 255, 255, 0.25) !important;
            transform: translateY(-2px) !important;
            box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
          }
          
          .icon-container:hover {
            transform: translateY(-8px) scale(1.05) !important;
            box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15) !important;
            background: linear-gradient(135deg, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.2)) !important;
          }
          
          .stat-card:hover {
            transform: translateY(-8px) scale(1.02) !important;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
            border-color: #3b82f6 !important;
          }
          
          .stat-card:hover .stat-icon {
            animation: pulse 2s infinite;
            transform: scale(1.1);
          }
          
          .main-card:hover {
            transform: translateY(-12px) !important;
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
            border-color: #3b82f6 !important;
          }
          
          .main-card:hover .card-gradient {
            height: 6px !important;
            background: linear-gradient(135deg, #2563eb, #1d4ed8) !important;
          }
          
          .main-card:hover .card-title-icon {
            transform: scale(1.1) rotate(5deg);
          }
          
          .cta-button:hover {
            transform: translateY(-4px) scale(1.05) !important;
            box-shadow: 0 20px 25px -5px rgba(59, 130, 246, 0.6), 0 10px 10px -5px rgba(59, 130, 246, 0.4) !important;
            background: linear-gradient(135deg, #2563eb, #1d4ed8) !important;
          }
          
          .cta-button:hover .button-arrow {
            transform: translateX(5px) rotate(180deg);
          }
          
          .list-item:hover .list-icon {
            transform: scale(1.2);
            transition: transform 0.3s ease;
          }
          
          .cta-card:hover {
            transform: translateY(-5px) !important;
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.15) !important;
          }
          
          ${responsiveCSS}
        `}
      </style>
      
      <div style={styles.heroSection}>
        <div style={styles.heroPattern}></div>
        
        <Link to="/ai-services" style={styles.backButton} className="back-button">
          <ArrowLeft style={{ width: '14px', height: '14px', marginRight: '6px' }} />
          Back to AI Services
        </Link>
        
        <div style={styles.heroContent} className="hero-content">
          <div style={styles.iconContainer} className="icon-container">
            <Icon style={{ width: '30px', height: '30px', color: '#ffffff' }} />
          </div>
          
          <h1 style={styles.title} className="hero-title">{service.title}</h1>
          <p style={styles.description} className="hero-description">{service.description}</p>
        </div>
      </div>

      <div style={styles.contentSection} className="content-section">
        {/* Stats Section */}
        <div style={styles.statsSection} className="stats-section">
          <div style={styles.statCard} className="stat-card">
            <Zap style={{...styles.statIcon}} className="stat-icon" />
            <div style={styles.statTitle} className="stat-title">Performance</div>
            <div style={styles.statValue} className="stat-value">99.9%</div>
          </div>
          <div style={styles.statCard} className="stat-card">
            <Star style={{...styles.statIcon}} className="stat-icon" />
            <div style={styles.statTitle} className="stat-title">Success Rate</div>
            <div style={styles.statValue} className="stat-value">95%+</div>
          </div>
          <div style={styles.statCard} className="stat-card">
            <TrendingUp style={{...styles.statIcon}} className="stat-icon" />
            <div style={styles.statTitle} className="stat-title">ROI Increase</div>
            <div style={styles.statValue} className="stat-value">250%</div>
          </div>
          <div style={styles.statCard} className="stat-card">
            <Users style={{...styles.statIcon}} className="stat-icon" />
            <div style={styles.statTitle} className="stat-title">Clients Served</div>
            <div style={styles.statValue} className="stat-value">500+</div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div style={styles.mainGrid} className="main-grid">
          <div style={styles.card} className="main-card">
            <div style={styles.cardGradient} className="card-gradient"></div>
            <h2 style={styles.cardTitle} className="card-title">
              <CheckCircle style={styles.cardTitleIcon} className="card-title-icon" />
              Key Benefits
            </h2>
            <ul style={{ listStyle: 'none', padding: '0', margin: '0' }}>
              {service.keyBenefits.map((benefit, index) => (
                <li key={index} style={styles.listItem} className="list-item">
                  <CheckCircle style={{ ...styles.listIcon, color: '#10b981' }} className="list-icon" />
                  {benefit}
                </li>
              ))}
            </ul>
          </div>

          <div style={styles.card} className="main-card">
            <div style={styles.cardGradient} className="card-gradient"></div>
            <h2 style={styles.cardTitle} className="card-title">
              <Lightbulb style={styles.cardTitleIcon} className="card-title-icon" />
              Real-World Applications
            </h2>
            <ul style={{ listStyle: 'none', padding: '0', margin: '0' }}>
              {service.useCases.map((useCase, index) => (
                <li key={index} style={styles.listItem} className="list-item">
                  <Lightbulb style={{ ...styles.listIcon, color: '#f59e0b' }} className="list-icon" />
                  {useCase}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div style={styles.ctaSection} className="cta-section">
        <div style={{...styles.ctaCard, transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'}} className="cta-card">
          <div style={styles.ctaGradient}></div>
          <h2 style={styles.ctaTitle} className="cta-title">Ready to Transform Your Business?</h2>
          <p style={styles.ctaText} className="cta-text">
            Let's discuss how {service.title.toLowerCase()} can revolutionize your operations, 
            boost efficiency, and drive unprecedented growth for your organization.
          </p>
          <Link to="/contact" style={styles.button} className="cta-button">
            Get Started Today
            <ArrowLeft style={{ width: '18px', height: '18px', marginLeft: '8px', transform: 'rotate(180deg)', transition: 'transform 0.3s ease' }} className="button-arrow" />
          </Link>
        </div>
      </div>
    </div>
  );
}